/**
 * /academy/challenge — « Défi du jour »: 10 CE questions, the same for everyone that day, against
 * the clock. Start → questions; submit → score, XP earned and the leaderboard of the day (first
 * name + initial only).
 */

import {
  ApiError,
  type ChallengeView,
  academyChallengeCreate,
  getAcademyChallengeRetrieveQueryKey,
  getAcademyProgressRetrieveQueryKey,
  useAcademyChallengeRetrieve,
} from "@agencyos/api-client";
import { challengeXp, hasPictures, parseChallengeReview } from "@agencyos/core";
import { Badge, Button, GlassCard, Skeleton } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { BookOpenText, Crown, Sparkles, Swords, Timer as TimerIcon, Trophy } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { readStorage, writeStorage } from "../lib/storage";
import { ChallengeReview } from "./ChallengeReview";
import { ExamPictureView, PictureOptions } from "./ExamPicture";
import { parseChallengeQuestions, parseEntry, parseLeaderboard } from "./progressTypes";
import { Timer } from "./shared";
import { clock } from "./text";
import { CardTitle, PageHead, Ring } from "./ui";

const LETTERS = ["A", "B", "C", "D", "E", "F"];
const draftKey = (id: string) => `academy.challenge.${id}`;

function readDraft(id: string): Record<string, number> {
  try {
    const raw: unknown = JSON.parse(readStorage(draftKey(id)) ?? "{}");
    if (typeof raw !== "object" || raw === null) return {};
    const out: Record<string, number> = {};
    for (const [key, value] of Object.entries(raw)) if (typeof value === "number") out[key] = value;
    return out;
  } catch {
    return {};
  }
}

/** Seconds left before the end of the challenge, counted from its start on the server. */
export function useChallengeClock(startedAt: string, minutes: number, running: boolean): number {
  const deadline = Date.parse(startedAt) + minutes * 60_000;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!running) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [running]);
  if (Number.isNaN(deadline)) return minutes * 60;
  return Math.max(0, Math.ceil((deadline - now) / 1000));
}

export function Leaderboard({ view, total }: { view: ChallengeView; total: number }) {
  const { t } = useTranslation();
  const board = parseLeaderboard(view.leaderboard);
  const podium = board.top.filter((row) => row.position <= 3);
  const rest = board.top.filter((row) => row.position > 3);
  // Podium order as on a real one: 2nd, 1st, 3rd.
  const ordered = [podium[1], podium[0], podium[2]].filter((row): row is NonNullable<typeof row> => Boolean(row));
  return (
    <GlassCard as="section" className="ac-card ac-board" aria-labelledby="academy-board">
      <div className="ac-card__head">
        <CardTitle id="academy-board" icon={<Trophy size={16} />} tone="warning">
          {t("academy.challenge.board")}
        </CardTitle>
      </div>
      <p className="ac-secondary">
        {board.myPosition !== null
          ? t("academy.challenge.myPosition", { position: board.myPosition, count: board.participants })
          : t("academy.challenge.participants", { count: board.participants })}
      </p>
      {board.top.length === 0 ? (
        <div className="ac-empty">
          <Crown aria-hidden size={22} />
          <p className="ac-secondary">{t("academy.challenge.boardEmpty")}</p>
        </div>
      ) : (
        <>
          <ol className="ac-podium">
            {ordered.map((row) => (
              <li key={row.position} className={`ac-podium__place ac-podium__place--${row.position}${row.me ? " ac-podium__place--me" : ""}`}>
                <span className="ac-podium__avatar" aria-hidden="true">
                  {row.name.slice(0, 1)}
                  {row.position === 1 && <Crown className="ac-podium__crown" size={16} />}
                </span>
                <span className="ac-podium__name">{row.name}</span>
                {row.me && <Badge tone="accent">{t("academy.challenge.you")}</Badge>}
                <span className="ac-podium__score ao-num">{t("academy.challenge.scoreShort", { score: row.score, total })}</span>
                <span className="ac-podium__block ao-num" aria-hidden="true">
                  {row.position}
                </span>
                <span className="visually-hidden">{t("academy.ui.challenge.place", { n: row.position })}</span>
              </li>
            ))}
          </ol>
          {rest.length > 0 && (
            <ol className="ac-board__list" start={4}>
              {rest.map((row) => (
                <li key={row.position} className={`ac-board__row${row.me ? " ac-board__row--me" : ""}`}>
                  <span className="ac-board__pos ao-num">{row.position}</span>
                  <span className="ac-board__name">
                    {row.name}
                    {row.me && <Badge tone="accent">{t("academy.challenge.you")}</Badge>}
                  </span>
                  <span className="ao-num">{t("academy.challenge.scoreShort", { score: row.score, total })}</span>
                  <span className="ao-num ac-caption">{clock(row.seconds)}</span>
                </li>
              ))}
            </ol>
          )}
        </>
      )}
    </GlassCard>
  );
}

export function ChallengePage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const query = useAcademyChallengeRetrieve(undefined, { query: { retry: false } });
  const view = query.data?.status === 200 ? query.data.data : undefined;
  const entry = useMemo(() => parseEntry(view?.entry), [view]);
  const questions = useMemo(() => parseChallengeQuestions(view?.questions), [view]);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submitted = useRef(false);
  const running = Boolean(entry && !entry.finished);
  const left = useChallengeClock(entry?.startedAt ?? "", view?.minutes ?? 10, running);

  useEffect(() => {
    if (view?.id) setAnswers(readDraft(view.id));
  }, [view?.id]);

  const act = useCallback(
    async (action: "start" | "submit", sent?: Record<string, number>) => {
      setBusy(true);
      setError(null);
      try {
        const response = await academyChallengeCreate(action === "submit" ? { action, answers: sent ?? {} } : { action });
        queryClient.setQueryData(getAcademyChallengeRetrieveQueryKey(), response);
        if (action === "submit") {
          writeStorage(draftKey(response.data.id), null);
          void queryClient.invalidateQueries({ queryKey: getAcademyProgressRetrieveQueryKey() });
        }
      } catch (failure) {
        setError(failure instanceof ApiError && failure.detail ? failure.detail : t("common.error"));
        if (failure instanceof ApiError && failure.status === 400) void query.refetch();
      } finally {
        setBusy(false);
      }
    },
    [queryClient, query, t],
  );

  // Time up: the answers given so far are sent (the server still checks the time).
  useEffect(() => {
    if (running && left === 0 && !submitted.current) {
      submitted.current = true;
      void act("submit", answers);
    }
  }, [running, left, act, answers]);

  if (query.isLoading) return <Skeleton style={{ height: 280 }} />;
  if (query.error instanceof ApiError && query.error.status === 404) {
    return (
      <div className="ac-page">
        <div className="ac-surface ac-card ac-card--center ac-empty">
          <span className="ac-challenge-hero__icon" aria-hidden="true">
            <Swords size={26} />
          </span>
          <h1 className="ac-title-2">{t("academy.challenge.title")}</h1>
          <p className="ac-secondary">{t("academy.challenge.none")}</p>
        </div>
      </div>
    );
  }
  if (!view) {
    return (
      <GlassCard className="ac-card ac-card--center">
        <p className="ko">{t("common.error")}</p>
        <Button variant="secondary" onClick={() => void query.refetch()}>
          {t("common.retry")}
        </Button>
      </GlassCard>
    );
  }

  const choose = (questionId: string, choice: number) => {
    const next = { ...answers, [questionId]: choice };
    setAnswers(next);
    writeStorage(draftKey(view.id), JSON.stringify(next));
  };
  const total = entry?.total || questions.length || 10;
  const answeredCount = questions.filter((q) => answers[q.id] !== undefined).length;

  const xpWon = entry?.finished ? challengeXp(view.xp, entry.score, entry.total) : 0;

  return (
    <div className="ac-page">
      <PageHead
        eyebrow={t("academy.challenge.eyebrow")}
        title={view.title || t("academy.challenge.title")}
        subtitle={<p>{t("academy.challenge.rules", { count: total, minutes: view.minutes, xp: view.xp })}</p>}
      />

      {!entry && (
        <section className="ac-challenge-hero" aria-labelledby="academy-challenge-start">
          <div className="ac-challenge-hero__icon" aria-hidden="true">
            <Swords size={28} />
          </div>
          <div className="ac-challenge-hero__text">
            <h2 id="academy-challenge-start" className="ac-title-2">
              {t("academy.challenge.ready")}
            </h2>
            <p className="ac-secondary">{t("academy.challenge.startHint")}</p>
            <ul className="ac-challenge-hero__facts">
              <li>
                <BookOpenText aria-hidden size={15} /> {t("academy.mocks.questions", { count: total })}
              </li>
              <li>
                <TimerIcon aria-hidden size={15} /> {t("academy.mocks.minutes", { count: view.minutes })}
              </li>
              <li>
                <Sparkles aria-hidden size={15} /> {t("academy.ui.challenge.upTo", { xp: view.xp })}
              </li>
            </ul>
          </div>
          <Button size="lg" onClick={() => void act("start")} disabled={busy}>
            {busy ? t("common.loading") : t("academy.challenge.start")}
          </Button>
        </section>
      )}

      {entry && !entry.finished && (
        <section className="ac-challenge-run" aria-labelledby="academy-challenge-q">
          <form
            className="ac-challenge-run__form"
            onSubmit={(event) => {
              event.preventDefault();
              submitted.current = true;
              void act("submit", answers);
            }}
          >
            <div className="ac-challenge-run__bar">
              <h2 id="academy-challenge-q" className="ac-headline ao-num">
                {t("academy.challenge.answered", { answered: answeredCount, total: questions.length })}
              </h2>
              <div className="ac-progress" aria-hidden="true">
                <span style={{ transform: `scaleX(${questions.length ? answeredCount / questions.length : 0})` }} />
              </div>
              <Timer seconds={left} label={t("academy.player.timeLeft")} />
            </div>
            <ol className="ac-challenge-list">
              {questions.map((q, index) => (
                <li key={q.id} className="ac-question__sheet">
                  <p className="ac-player__eyebrow ao-num" aria-hidden="true">
                    {t("academy.player.progress", { n: index + 1, total: questions.length })}
                  </p>
                  {q.document && (
                    <div className="ac-document ac-document--small" lang="fr">
                      {q.document}
                    </div>
                  )}
                  <ExamPictureView picture={q.image} lang="fr" size="medium" />
                  <fieldset className="plain-fieldset ac-options-set">
                    <legend className="ac-stem ac-stem--small" lang="fr">
                      <span className="visually-hidden">{t("academy.player.progress", { n: index + 1, total: questions.length })} </span>
                      {q.stem}
                    </legend>
                    {hasPictures(q.option_images) ? (
                      <PictureOptions
                        name={`c-${q.id}`}
                        options={q.options}
                        images={q.option_images}
                        value={answers[q.id] ?? null}
                        onChange={(i) => choose(q.id, i)}
                        lang="fr"
                      />
                    ) : (
                    <div className="ac-options" lang="fr">
                      {q.options.map((option, i) => (
                        <label key={i} className={`ac-option${answers[q.id] === i ? " ac-option--on" : ""}`}>
                          <input type="radio" name={`c-${q.id}`} checked={answers[q.id] === i} onChange={() => choose(q.id, i)} />
                          <span className="ac-option__letter" aria-hidden>
                            {LETTERS[i]}
                          </span>
                          <span className="ac-option__text">{option}</span>
                        </label>
                      ))}
                    </div>
                    )}
                  </fieldset>
                </li>
              ))}
            </ol>
            <div className="ac-question__actions">
              <Button type="submit" size="lg" disabled={busy}>
                {busy ? t("common.loading") : t("academy.challenge.submit")}
              </Button>
            </div>
          </form>
        </section>
      )}

      {error && (
        <p className="ko" role="alert">
          {error}
        </p>
      )}

      {entry?.finished ? (
        <div className="ac-grid ac-challenge-result">
          <section className="ac-challenge-done ac-span-5" aria-labelledby="academy-challenge-score">
            <Ring ratio={entry.total ? entry.score / entry.total : 0} size={148} stroke={12} tone="warning">
              <span className="ac-challenge-done__score ao-num" role="status">
                {t("academy.challenge.score", { score: entry.score, total: entry.total })}
              </span>
            </Ring>
            <div className="ac-challenge-done__text">
              <h2 id="academy-challenge-score" className="ac-title-2">
                {t("academy.challenge.done")}
              </h2>
              <ul className="ac-challenge-hero__facts">
                <li>
                  <TimerIcon aria-hidden size={15} /> {t("academy.challenge.time", { time: clock(entry.seconds) })}
                </li>
                <li className="ac-xp">
                  <Sparkles aria-hidden size={15} /> {t("academy.challenge.xp", { count: xpWon })}
                </li>
              </ul>
              <p className="ac-footnote">{t("academy.challenge.comeBack")}</p>
            </div>
          </section>
          <div className="ac-span-7">
            <Leaderboard view={view} total={total} />
          </div>
        </div>
      ) : null}
      {entry?.finished && <ChallengeReview rows={parseChallengeReview(view.review)} lang="fr" />}
      {!entry && <Leaderboard view={view} total={total} />}
    </div>
  );
}
